/* =====================================================================
 * useRouteSync — URL pathname ↔ chatStore.activeMode 양방향 동기화
 *
 * WorkspaceShell 내에서 호출하여:
 *  1. URL이 바뀌면 → chatStore.activeMode / activeConversation 갱신
 *  2. 사이드바 등에서 activeMode가 바뀌면 → URL을 navigate()로 갱신
 *
 * 무한 루프를 방지하기 위해 "변경 원인 추적" ref를 사용합니다.
 * ===================================================================== */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigationStore } from '@auw/stores';
import { upsertConversationEntity } from '@auw/stores';
import { isWorkspaceItemUnavailableError } from '@auw/workspace-utils';
import { getRouteByPath, extractIdFromPath, modeToPath } from '@auw/workspace-utils';
import { resolveWorkspaceRoute } from '../workspace/navigation/useWorkspaceRoute';

/**
 * URL → Store 동기화 (라우트가 변경되면 store를 업데이트)
 *
 * Layout 레벨에서 한 번만 마운트합니다.
 */
export function useRouteSyncToStore(enabled = true): void {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const setActiveMode = useChatStore((s) => s.setActiveMode);
  const setActiveConversation = useChatStore((s) => s.setActiveConversation);
  const rememberModeSelection = useWorkspaceNavigationStore((s) => s.rememberModeSelection);
  const clearRememberedModeSelection = useWorkspaceNavigationStore((s) => s.clearRememberedModeSelection);

  const prevPathRef = useRef('');
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    const route = resolveWorkspaceRoute(pathname);
    if (!route.entry || !route.mode) return;
    const routeMode = route.mode;

    if (pathname !== prevPathRef.current) {
      prevPathRef.current = pathname;
      const currentMode = useChatStore.getState().activeMode;
      if (currentMode !== routeMode) {
        setActiveMode(routeMode);
      }
    }

    if (!route.itemId) {
      const currentState = useChatStore.getState();
      const activeConversation = currentState.activeId
        ? currentState.conversations[currentState.activeId] ?? null
        : null;
      if (currentState.activeId !== null && activeConversation?.mode === routeMode) {
        setActiveConversation(null, { syncMode: false });
      }
      return;
    }

    const id = route.itemId;
    rememberModeSelection(routeMode, id);

    const currentState = useChatStore.getState();
    const currentActiveId = currentState.activeId;
    if (id === currentActiveId) return;

    if (currentState.conversations[id]) {
      setActiveConversation(id);
      return;
    }

    /* 대화가 로드된 아이템에 없는 경우: 개별 fetch (페이지네이션 범위 밖)
       - stale 응답이 현재 선택을 덮어쓰지 않도록 sequence 가드 적용 */
    const seq = ++fetchSeqRef.current;
    const requestedId = id;

    Promise.all([
      import('@auw/api-client'),
      import('@auw/api-client'),
    ]).then(([wsApi, { itemToConversation }]) => {
      wsApi.getItem(requestedId)
        .then((item) => {
          if (!item) return;
          if (seq !== fetchSeqRef.current) return;

          const latestPathId = extractIdFromPath(window.location.pathname);
          if (latestPathId !== requestedId) return;

          const conv = itemToConversation(item);
          useChatStore.setState((s) => upsertConversationEntity(s, conv, { prepend: true }));
          if (useChatStore.getState().activeId !== requestedId) {
            setActiveConversation(requestedId);
          }
        })
        .catch((err) => {
          if (!isWorkspaceItemUnavailableError(err)) return;
          if (seq !== fetchSeqRef.current) return;

          const latestPathId = extractIdFromPath(window.location.pathname);
          if (latestPathId !== requestedId) return;

          clearRememberedModeSelection(routeMode);
          navigate(modeToPath(routeMode), { replace: true });
        });
    }).catch(() => {
      // dynamic import 실패는 라우팅 자체를 막지 않음
    });
  }, [enabled, clearRememberedModeSelection, navigate, pathname, rememberModeSelection, setActiveMode, setActiveConversation]);
}

/**
 * Store → URL 동기화 (사이드바에서 모드 전환 시 URL 갱신)
 *
 * activeMode가 바뀌었는데 URL이 아직 반영되지 않았으면 navigate합니다.
 */
export function useStoreSyncToRoute(enabled = true): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeMode = useChatStore((s) => s.activeMode);
  const activeId = useChatStore((s) => s.activeId);
  const rememberModeSelection = useWorkspaceNavigationStore((s) => s.rememberModeSelection);

  const isInternalNav = useRef(false);
  const hasMountedRef = useRef(false);
  const prevPathRef = useRef(pathname);

  useEffect(() => {
    if (!enabled) return;
    /* "전문 에이전트" (`/specialized/*`) 같은 모드-직교 라우트에서는
     * activeMode → URL sync 대상이 아니다. ROUTE_ENTRIES 에 등록되지
     * 않은 path 에서 activeMode 기준으로 navigate 하면 사용자가 방금
     * 클릭한 specialized 페이지가 즉시 통합 에이전트 페이지로 튕긴다.
     * specialized 페이지는 매니페스트가 자체 path 를 관리하므로 sync
     * 자체를 건너뛴다. */
    if (pathname.startsWith('/specialized/')) return;

    // 마운트 직후 첫 렌더에서는 URL→Store 동기화가 먼저 진행되도록 건너뜀.
    // Store 초기값과 URL이 다른 경우(새로고침) 잘못된 리다이렉트를 방지한다.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      const entry = getRouteByPath(pathname);
      if (entry && entry.mode !== activeMode) return;
    }

    if (isInternalNav.current) {
      isInternalNav.current = false;
      prevPathRef.current = pathname;
      return;
    }

    const entry = getRouteByPath(pathname);
    const pathnameChanged = prevPathRef.current !== pathname;
    prevPathRef.current = pathname;

    // 브라우저 뒤로가기/앞으로가기, OAuth 리다이렉트처럼 URL이 먼저 바뀐 경우에는
    // 이 effect가 이전 activeMode로 다시 navigate 하지 않도록 한 틱 양보한다.
    if (pathnameChanged && entry) return;

    if (entry && entry.mode === activeMode) return;

    const targetPath = modeToPath(activeMode);
    if (pathname !== targetPath) {
      isInternalNav.current = true;
      navigate(targetPath, { replace: false });
    }
  }, [enabled, activeMode, pathname, navigate]);

  useEffect(() => {
    if (!enabled) return;
    const entry = getRouteByPath(pathname);
    if (!entry?.hasIdParam) return;
    if (entry.mode !== activeMode) return;
    const routeId = extractIdFromPath(pathname) ?? null;

    if (!activeId) {
      // URL에 이미 item id가 있으면 URL→Store 동기화가 완료될 때까지 기다린다.
      // store가 아직 비어있다는 이유만으로 base path로 되돌리면 깜빡임/루프가 생긴다.
      if (routeId) return;
      if (pathname !== entry.path) {
        navigate(entry.path, { replace: true });
      }
      return;
    }

    // activeId가 현재 모드와 일치하는 conversation인지 확인.
    // 다른 모드의 conversation id가 URL에 잘못 반영되는 것을 방지한다.
    const conv = useChatStore.getState().conversations[activeId];
    if (conv && conv.mode !== activeMode) return;

    if (routeId) {
      if (routeId === activeId) {
        rememberModeSelection(activeMode, activeId);
        return;
      }

      const routeConversation = useChatStore.getState().conversations[routeId];
      if (!routeConversation || routeConversation.mode === activeMode) {
        return;
      }
    }

    rememberModeSelection(activeMode, activeId);

    const expectedPath = `${entry.path}/${activeId}`;
    if (pathname !== expectedPath) {
      navigate(expectedPath, { replace: true });
    }
  }, [enabled, activeId, activeMode, navigate, pathname, rememberModeSelection]);
}

/**
 * WorkspaceRouteSync — URL↔activeMode 양방향 동기화를 **영속 레이아웃에서
 * 단 한 번** 마운트하기 위한 호스트 컴포넌트.
 *
 * 두 동기화 훅(`useRouteSyncToStore` / `useStoreSyncToRoute`)은 무한 루프를
 * 막기 위해 `useRef` 가드(prevPath / hasMounted / isInternalNav)에 의존한다.
 * 이 가드는 훅이 모드 전환 동안 **계속 살아있다**는 전제에서만 동작한다.
 *
 * route-first 레이아웃(`WorkspaceRouteLayouts`)은 모드마다 `WorkspaceModeChrome`
 * 를 새로 마운트하므로, 그 안에서 훅을 돌리면 모드를 바꿀 때마다 가드가
 * 초기화돼 `/widgets`↔`/agents` 핑퐁이 발생한다. 그래서 chrome 쪽 동기화는
 * 끄고(`syncRoute={false}`), 대신 Outlet 위의 영속 레이아웃에서 이 컴포넌트를
 * 한 번만 렌더해 가드가 네비게이션 사이에 유지되도록 한다.
 */
export function WorkspaceRouteSync(): null {
  useRouteSyncToStore();
  useStoreSyncToRoute();
  return null;
}

